import { useEffect, useMemo, useState } from 'react';
import { Document, pdfjs } from 'react-pdf/dist/cjs';
import 'react-pdf/dist/cjs/Page/AnnotationLayer.css';
import 'react-pdf/dist/cjs/Page/TextLayer.css';
import { Button, Icon, Popup } from 'semantic-ui-react';
import Loader from '../loader';
import ProvenanceDetails from '../provenanceDetails';
import ScaleControl from '../scaleControl';
import MultiPageView from '../pdfViewer/multiPageView';
import ThumbnailPanel from '../pdfViewer/thumbnailPanel';
import { SCALE_CONSTANTS } from '../pdfViewer/constants';
import { HighlightSpecType } from '../pdfViewer/types';
import { FieldProvenanceType, PageRegionType } from '../../schemas/report.schemas';
import './provenancePdfPanel.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type ProvenancePdfPanelPropsType = {
    provenance: FieldProvenanceType[] | null;
    // True while the sources for the visible reports are still being fetched. Until they arrive no
    // value is clickable, so the panel says so rather than inviting a click that cannot happen yet.
    loading?: boolean;
};

type PdfGroup = {
    documentUrl: string;
    label: string;
    entries: FieldProvenanceType[];
};

const noop = () => {};

const filenameFromUrl = (url: string, index: number): string => {
    try {
        const pathname = new URL(url).pathname;
        const name = decodeURIComponent(pathname.substring(pathname.lastIndexOf('/') + 1));
        return name || `Document ${index + 1}`;
    } catch {
        return `Document ${index + 1}`;
    }
};

// Page regions carry 0–100 percentages, matching the extraction overlay's convention, so they map
// straight onto the read-only highlight specs the PDF page renders. One entry can cover a value with
// several boxes — a figure wrapped across columns, say — so this flattens rather than maps. Every
// box carries its own entry's payload, which is what clicking it shows, whether the box is on the
// value or on its label.
const toRegionSpecs = (
    entry: FieldProvenanceType,
    entryIndex: number,
    regions: PageRegionType[],
    kind: string,
    className?: string,
): HighlightSpecType[] => {
    return regions.map((region, regionIndex) => ({
        // The kind belongs in the id: without it the two lists' boxes collide on the same key.
        id: `${entry.documentUrl}-${entry.page}-${entryIndex}-${kind}-${regionIndex}`,
        page: entry.page,
        xPercentage: region.x,
        yPercentage: region.y,
        widthPercentage: region.width,
        heightPercentage: region.height,
        tooltip: <ProvenanceDetails entry={entry} />,
        className,
    }));
};

// Where the figure is printed, and where the row label it was read against is printed. Both are
// drawn: seeing the label is how a reviewer judges that the figure came off the right row.
const toHighlightSpecs = (entries: FieldProvenanceType[]): HighlightSpecType[] => {
    // Paint every label before any value so value regions consistently win both rendering and hit
    // testing, including when the regions belong to different provenance entries.
    const labelSpecs = entries.flatMap((entry, entryIndex) =>
        toRegionSpecs(entry, entryIndex, entry.labelRegions, 'label', 'selection-box--label')
    );
    const valueSpecs = entries.flatMap((entry, entryIndex) =>
        toRegionSpecs(entry, entryIndex, entry.valueRegions, 'value')
    );

    return [...labelSpecs, ...valueSpecs];
};

function ProvenancePdfPanel({ provenance, loading }: ProvenancePdfPanelPropsType) {
    const groups = useMemo<PdfGroup[]>(() => {
        if (!provenance?.length) {
            return [];
        }
        const byUrl = new Map<string, FieldProvenanceType[]>();
        for (const entry of provenance) {
            const existing = byUrl.get(entry.documentUrl);
            if (existing) {
                existing.push(entry);
            } else {
                byUrl.set(entry.documentUrl, [entry]);
            }
        }
        return Array.from(byUrl.entries()).map(([documentUrl, entries], index) => ({
            documentUrl,
            label: filenameFromUrl(documentUrl, index),
            entries,
        }));
    }, [provenance]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [numberOfPages, setNumberOfPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [scale, setScale] = useState<number>(SCALE_CONSTANTS.DEFAULT_SCALE);
    const [thumbnailsVisible, setThumbnailsVisible] = useState(false);

    const activeGroup = groups[activeIndex];

    // The distinct pages this value was taken from, within the active document.
    const referencedPages = useMemo(() => {
        if (!activeGroup) {
            return [];
        }
        return [...new Set(activeGroup.entries.map((entry) => entry.page))].sort((a, b) => a - b);
    }, [activeGroup]);

    const highlightSpecs = useMemo(() => {
        return activeGroup ? toHighlightSpecs(activeGroup.entries) : [];
    }, [activeGroup]);

    // Entries the extractor could not place on the page draw no box, so there is nothing to click.
    // The toolbar keeps their payload reachable, rather than showing a document with no highlight
    // and no explanation for why.
    const unplacedEntries = useMemo(() => {
        return activeGroup ? activeGroup.entries.filter((entry) => entry.valueRegions.length === 0) : [];
    }, [activeGroup]);

    // Reset the active tab whenever a new value is selected.
    useEffect(() => {
        setActiveIndex(0);
    }, [provenance]);

    // Start each document at its first referenced page.
    useEffect(() => {
        setNumberOfPages(0);
        setCurrentPage(referencedPages[0] ?? 1);
    }, [activeGroup?.documentUrl, referencedPages]);

    if (!provenance?.length || !activeGroup) {
        return (
            <div className="provenance-pdf-panel provenance-pdf-panel--empty">
                {loading ? (
                    <>
                        <Loader />
                        <p>Loading sources…</p>
                    </>
                ) : (
                    <>
                        <Icon name="file pdf outline" size="huge" color="grey" />
                        <p>Select a value to see where it was extracted from.</p>
                    </>
                )}
            </div>
        );
    }

    const canShowThumbnails = referencedPages.length > 1;
    const thumbnailItems = referencedPages.map((page) => ({ page }));

    return (
        <div className="provenance-pdf-panel">
            <div className="provenance-pdf-panel__toolbar">
                {canShowThumbnails && (
                    <Button
                        icon="sidebar"
                        active={thumbnailsVisible}
                        onClick={() => setThumbnailsVisible((prev) => !prev)}
                        title="Toggle thumbnails"
                    />
                )}
                <ScaleControl scale={scale} onScaleChange={setScale} />
                {unplacedEntries.length > 0 && (
                    <Popup
                        on="click"
                        className="provenance-details-popup"
                        trigger={<Button icon="info circle" title="Not located on the page" />}
                        content={
                            <div className="provenance-pdf-panel__unplaced">
                                {unplacedEntries.map((entry, index) => (
                                    <ProvenanceDetails key={`${entry.descriptorId}-${index}`} entry={entry} />
                                ))}
                            </div>
                        }
                    />
                )}
            </div>

            {groups.length > 1 && (
                <div className="provenance-pdf-panel__tabs">
                    {groups.map((group, index) => (
                        <button
                            key={group.documentUrl}
                            type="button"
                            className={`provenance-pdf-panel__tab${
                                index === activeIndex ? ' provenance-pdf-panel__tab--active' : ''
                            }`}
                            onClick={() => setActiveIndex(index)}
                            title={group.label}
                        >
                            {group.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="provenance-pdf-panel__body">
                <Document
                    key={activeGroup.documentUrl}
                    loading={<Loader />}
                    file={activeGroup.documentUrl}
                    onLoadSuccess={({ numPages }: { numPages: number }) => setNumberOfPages(numPages)}
                >
                    {thumbnailsVisible && canShowThumbnails && (
                        <ThumbnailPanel
                            items={thumbnailItems}
                            currentPage={currentPage}
                            onPageSelect={setCurrentPage}
                        />
                    )}
                    <div className="provenance-pdf-panel__pages">
                        <MultiPageView
                            numberOfPages={numberOfPages}
                            pages={referencedPages}
                            currentPage={currentPage}
                            selectionDisabled={true}
                            handleExtraction={noop}
                            extractionSpecs={highlightSpecs}
                            scale={scale}
                        />
                    </div>
                </Document>
            </div>
        </div>
    );
}

export default ProvenancePdfPanel;
